import { QueryClient } from '@tanstack/react-query'
import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client'

/**
 * RBAC cache persistence configuration
 *
 * We persist the RBAC query cache to localStorage to avoid
 * unnecessary API calls on page refresh. This is safe because:
 * 1. RBAC data changes infrequently
 * 2. We use a reasonable maxAge to bust stale caches
 * 3. The data is not sensitive (just roles/permissions)
 */
const PERSIST_KEY = 'aerialflow-query-cache'
const PERSIST_MAX_AGE = 1000 * 60 * 30 // 30 minutes (matches gcTime)

/**
 * Async localStorage wrapper for the persister
 * Wrapped in async to satisfy @tanstack/react-query-persist-client API
 */
const asyncLocalStorage = {
  getItem: async (key: string): Promise<string | null> => {
    if (typeof window === 'undefined') return null
    return window.localStorage.getItem(key)
  },
  setItem: async (key: string, value: string): Promise<void> => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(key, value)
  },
  removeItem: async (key: string): Promise<void> => {
    if (typeof window === 'undefined') return
    window.localStorage.removeItem(key)
  },
}

/**
 * Create async storage persister for React Query
 *
 * Only persists queries with 'rbac' in their query key to avoid
 * bloating localStorage with all query data.
 */
export const queryPersister: Persister = {
  persistClient: async (client: PersistedClient) => {
    // Filter to only persist RBAC-related queries
    const filteredClient: PersistedClient = {
      ...client,
      clientState: {
        ...client.clientState,
        queries: client.clientState.queries.filter((query) =>
          query.queryKey.includes('rbac')
        ),
        // Don't persist mutations
        mutations: [],
      },
    }
    await asyncLocalStorage.setItem(PERSIST_KEY, JSON.stringify(filteredClient))
  },
  restoreClient: async () => {
    const data = await asyncLocalStorage.getItem(PERSIST_KEY)
    if (!data) return undefined

    try {
      const parsed = JSON.parse(data) as PersistedClient

      // Check if cache is expired
      if (Date.now() - parsed.timestamp > PERSIST_MAX_AGE) {
        await asyncLocalStorage.removeItem(PERSIST_KEY)
        return undefined
      }

      return parsed
    } catch {
      // Invalid JSON, remove corrupted cache
      await asyncLocalStorage.removeItem(PERSIST_KEY)
      return undefined
    }
  },
  removeClient: async () => {
    await asyncLocalStorage.removeItem(PERSIST_KEY)
  },
}

/**
 * Persist options for PersistQueryClientProvider
 */
export const persistOptions = {
  persister: queryPersister,
  maxAge: PERSIST_MAX_AGE,
  // Only persist RBAC queries
  dehydrateOptions: {
    shouldDehydrateQuery: (query: { queryKey: readonly unknown[] }) =>
      query.queryKey.includes('rbac'),
  },
}

/**
 * Default React Query client configuration for AerialFlow
 *
 * Settings optimized for mock API with reasonable caching
 * and user-friendly error handling
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes
      staleTime: 1000 * 60 * 5,
      // Keep unused data for 10 minutes
      gcTime: 1000 * 60 * 10,
      // Retry failed requests once
      retry: 1,
      // Refetch on window focus in development
      refetchOnWindowFocus: import.meta.env.DEV,
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
    },
  },
})
